import { Fragment, useEffect, useMemo } from 'react';
import { useUserStore } from '../../../../../modules/Auth/stores/authStore';
import { useExamExerciseListHook } from '../../../../hooks/examExerciseList';
import {
  ExerciseAction,
  IExercise,
  IExerciseAlternative,
} from '../../../../models/exercise';
import { getDisciplineName } from '../../../../utils/discipline';
import {
  getFilteredExercisesByArea,
  getFilteredExercisesByKnowledgeFields,
  getFilteredExercisesByOrder,
} from '../../../../utils/exercise';
import { Alert } from '../../../atoms/Alert';
import { Answer } from '../../../atoms/Answer';
import { Button } from '../../../atoms/Button';
import { Chip } from '../../../atoms/Chip';
import { Icon } from '../../../atoms/Icon';
import ExerciseAlternative from '../../../molecules/ExerciseAlternative';
import InnerHTMLContainer from '../../../molecules/InnerHTMLContainer';
import { Typography } from '../../../molecules/Typography';
import { ResolutionModal } from '../../ResolutionModal';
import { copyText } from '../../../../utils';
import { IVideo } from '../../../../models/video';
import { getVideoConfig } from '../../../../utils/video';
import { ContentActions } from '../../ContentActions';

interface ExamExerciseListProps {
  data: IExercise[];
  isLoading?: boolean;
  showExerciseType?: boolean;
  showExerciseArea?: boolean;
  showSubject?: boolean;
  selectedId?: string;
  orderBy: 'area' | 'knowledge' | 'order';
  clearSelected?: () => void;
  onExerciseChange: (exercise: IExercise, action?: ExerciseAction) => void;
}

export const ExamExerciseList = ({
  data,
  isLoading = false,
  showExerciseType = true,
  showExerciseArea = false,
  showSubject = false,
  selectedId,
  orderBy,
  clearSelected,
  onExerciseChange,
}: ExamExerciseListProps) => {
  const {
    exerciseTypes,
    resolutionData,
    isResolutionModalOpen,
    setExerciseAlternative,
    convertMathGraphs,
    showResolution,
    setResolutionModalOpen,
  } = useExamExerciseListHook();

  const { user } = useUserStore();

  useEffect(() => {
    convertMathGraphs();
  });

  useEffect(() => {
    if (selectedId && clearSelected) {
      const element = document.getElementById(selectedId);

      if (element) {
        const topPos = element.offsetTop - 75;
        window.scrollTo({ top: topPos, behavior: 'smooth' });
      }

      clearSelected();
    }
  }, [selectedId]);

  useEffect(() => {
    localStorage.setItem('exerciseListOrder', orderBy);
  }, [orderBy]);

  const filteredExercises = useMemo(() => {
    const savedOrderBy =
      orderBy || localStorage.getItem('exerciseListOrder') || 'order';

    switch (savedOrderBy) {
      case 'area':
        return getFilteredExercisesByArea(data);
      case 'knowledge':
        return getFilteredExercisesByKnowledgeFields(data);
      case 'order':
        return getFilteredExercisesByOrder(data);
      default:
        return data;
    }
  }, [orderBy, data]);

  return (
    <>
      {filteredExercises.map((exercise: IExercise, index) => {
        const resolutionVideo = exercise.resolution_video as IVideo;

        const { hasToken } = getVideoConfig(resolutionVideo);

        return (
          <div
            key={`exerciseList${exercise.id ?? index}`}
            id={exercise.id}
            className='flex w-full flex-col gap-xxl'
          >
            <div className='flex flex-col gap-lg'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-sm'>
                  <Answer
                    number={index + 1}
                    selected={exercise.selected}
                    variant={
                      exercise.exerciseType === 'discursive'
                        ? 'discursive'
                        : exercise?.correct && exercise?.done
                          ? 'correct'
                          : exercise?.correct === false && exercise?.done
                            ? 'incorrect'
                            : 'undefined'
                    }
                    done={exercise.done}
                  />
                  <div className='flex flex-col gap-sm'>
                    <div className='flex gap-sm'>
                      {showExerciseArea && (
                        <Typography size='small'>
                          {exercise.area ??
                            getDisciplineName(exercise?.ancestors?.area?.slug)}
                        </Typography>
                      )}
                      {showSubject &&
                        exercise.subjects?.map((subject) => {
                          return (
                            <Typography
                              key={`subject${subject._id}`}
                              size='small'
                            >
                              {subject.title}
                            </Typography>
                          );
                        })}
                    </div>
                    {showExerciseType && exercise.exerciseType && (
                      <Chip variant='negative'>{`Esta resposta é ${
                        exerciseTypes[exercise.exerciseType]
                      }`}</Chip>
                    )}
                    {user?.exercisePercentage &&
                      exercise.exerciseType !== 'discursive' &&
                      !isNaN(exercise.correctPercentage) && (
                        <Chip variant='negative'>{`${(
                          exercise.correctPercentage * 100
                        )
                          .toFixed(2)
                          .toString()}% de acerto`}</Chip>
                      )}
                  </div>
                </div>
                <ContentActions.Root
                  contentType={'Exercise'}
                  contentId={exercise.id}
                >
                  <ContentActions.Report />
                </ContentActions.Root>
              </div>
              <InnerHTMLContainer
                data={exercise.statement?.replaceAll("{STORAGE_BASE_URL}", "https://storage.aprovatotal.com.br") ?? ''}
                type='courseProgram'
                className={`
                  exam-exercise-statement hyphens-auto text-mono-color-darkest xs:break-keep [&>p__img]:bg-[#fff] 
                  ${user?.isDarkMode && '[&_td]:!bg-[transparent] [&_th]:!bg-[transparent]' }
                `}
              />
              <div className={'flex justify-end w-full'}>
                <Typography
                  size='mini'
                  className='cursor-pointer self-end text-right w-fit'
                  onClick={() => copyText(exercise.id)}
                >
                  Código: {exercise.id}
                </Typography>
              </div>

              {hasToken && resolutionVideo?.defaultPlayer !== 'samba' && (
                <Button
                  variant='text'
                  className='p-0'
                  onClick={() => {
                    exercise.done = true;
                    showResolution(exercise);
                  }}
                >
                  <Icon Icon='videocam' type='material' className='text-md' />
                  Assistir resolução
                </Button>
              )}
            </div>
            {exercise.done && !exercise.hasAlternativeSelected && (
              <Alert variant='error' title='Questão em branco' message='' />
            )}
            <div className='flex flex-col gap-md'>
              {exercise.alternatives?.map(
                (alternative: IExerciseAlternative) => {
                  const isCorrect = alternative?.correct || alternative?.correction;

                  if (alternative) {
                    return (
                      <Fragment key={alternative.id}>
                        <ExerciseAlternative
                          isCorrect={isCorrect}
                          isEmptyExercise={!exercise.hasAlternativeSelected}
                          alternativeId={alternative.id}
                          text={alternative.text}
                          value={alternative.text}
                          type={exercise.exerciseType || exercise?.exercise_type}
                          selected={alternative.selected}
                          status={alternative.status}
                          isShowingCorrectAnswer={!exercise.hasAlternativeSelected || exercise.showAnswer || (!exercise.correct && exercise.done)}
                          isLoading={isLoading}
                          onSelected={() => {
                            onExerciseChange(
                              setExerciseAlternative(alternative.id, exercise),
                              'selectAlternative'
                            );
                          }}
                        />
                        {(exercise.showAnswer || !exercise.hasAlternativeSelected) && isCorrect && exercise?.resolution && (
                          <div className='ml-10 rounded-md border-hairline border-mono-color-medium p-5'>
                            <InnerHTMLContainer
                              data={exercise.resolution}
                              type='courseProgram'
                              className='text-mono-color-darkest !break-words'
                            />
                          </div>
                        )}
                      </Fragment>
                    );
                  }
                }
              )}
            </div>
          </div>
        );
      })}
      <ResolutionModal
        resolutionVideo={resolutionData.resolutionVideo}
        exerciseId={resolutionData.exerciseId}
        isOpen={isResolutionModalOpen}
        onClose={() => setResolutionModalOpen(false)}
      />
    </>
  );
};
